import React, { useState } from 'react';
import { Terminal, Copy, Check, Sparkles } from 'lucide-react';

interface CodeSnippetGeneratorProps {
  apiKey?: string;
  applicationId?: string;
  gatewayUrl?: string;
  sampleEndpoint?: string;
}

type SupportedLanguage =
  | 'curl'
  | 'javascript'
  | 'axios'
  | 'python'
  | 'php'
  | 'go'
  | 'java'
  | 'csharp';

export const CodeSnippetGenerator: React.FC<CodeSnippetGeneratorProps> = ({
  apiKey = 'mk_test_suaChaveAqui...',
  applicationId,
  gatewayUrl,
  sampleEndpoint = '/v1/rupe/consultar?sistemaOrigem=ANRES&idOrigem=1004C/ANR/2025&numeroDC=60201260201092866562&tipoDC=RUPE',
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('curl');
  const [copied, setCopied] = useState(false);

  const resolvedGatewayUrl =
    gatewayUrl ||
    (import.meta as any).env?.VITE_KONG_GATEWAY_URL ||
    (import.meta as any).env?.VITE_API_BASE_URL ||
    (typeof window !== 'undefined' &&
    (window.location.origin.includes('tatusolutions.com') ||
      window.location.hostname.includes('tatusolutions.com'))
      ? 'https://api-gateway.tatusolutions.com'
      : 'http://localhost:8000');

  const fullUrl = `${resolvedGatewayUrl}${sampleEndpoint}`;

  const languages: { id: SupportedLanguage; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'Fetch' },
    { id: 'axios', label: 'Node (Axios)' },
    { id: 'python', label: 'Python' },
    { id: 'php', label: 'PHP' },
    { id: 'go', label: 'Go' },
    { id: 'java', label: 'Java' },
    { id: 'csharp', label: 'C# / .NET' },
  ];

  const getSnippet = () => {
    switch (selectedLanguage) {
      case 'javascript':
        return `// Executar com Node.js (v18+) ou no Browser nativo
const response = await fetch("${fullUrl}", {
  method: "GET",
  headers: {
    "X-API-Key": "${apiKey}",
    "Accept": "application/json"${applicationId ? `,\n    "X-Application-ID": "${applicationId}"` : ''}
  }
});

const data = await response.json();
console.log("Status:", response.status);
console.log("Resposta da API:", data);`;

      case 'axios':
        return `// Executar com Node.js (npm install axios)
const axios = require('axios');

async function callApi() {
  try {
    const response = await axios.get("${fullUrl}", {
      headers: {
        "X-API-Key": "${apiKey}",
        "Accept": "application/json"${applicationId ? `,\n        "X-Application-ID": "${applicationId}"` : ''}
      }
    });
    console.log("Dados:", response.data);
  } catch (error) {
    console.error("Erro na chamada:", error.response?.data || error.message);
  }
}

callApi();`;

      case 'python':
        return `# Executar com Python 3 (pip install requests)
import requests

url = "${fullUrl}"
headers = {
    "X-API-Key": "${apiKey}",
    "Accept": "application/json"${applicationId ? `,\n    "X-Application-ID": "${applicationId}"` : ''}
}

try:
    response = requests.get(url, headers=headers, timeout=10)
    response.raise_for_status()
    print("Status:", response.status_code)
    print("Resposta JSON:", response.json())
except requests.exceptions.RequestException as e:
    print("Erro na requisição:", e)`;

      case 'php':
        return `<?php
// Executar com PHP 8+ (cURL nativo)
$curl = curl_init();

curl_setopt_array($curl, [
    CURLOPT_URL => "${fullUrl}",
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_ENCODING => "",
    CURLOPT_MAXREDIRS => 10,
    CURLOPT_TIMEOUT => 30,
    CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
    CURLOPT_CUSTOMREQUEST => "GET",
    CURLOPT_HTTPHEADER => [
        "X-API-Key: ${apiKey}",
        "Accept: application/json"${applicationId ? `,\n        "X-Application-ID: ${applicationId}"` : ''}
    ],
]);

$response = curl_exec($curl);
$err = curl_error($curl);
curl_close($curl);

if ($err) {
    echo "Erro cURL: " . $err;
} else {
    echo $response;
}`;

      case 'go':
        return `package main

import (
    "fmt"
    "io"
    "net/http"
    "os"
)

func main() {
    url := "${fullUrl}"
    req, err := http.NewRequest("GET", url, nil)
    if err != nil {
        fmt.Println("Erro ao criar requisição:", err)
        os.Exit(1)
    }

    req.Header.Add("X-API-Key", "${apiKey}")
    req.Header.Add("Accept", "application/json")${applicationId ? `\n    req.Header.Add("X-Application-ID", "${applicationId}")` : ''}

    res, err := http.DefaultClient.Do(req)
    if err != nil {
        fmt.Println("Erro na chamada:", err)
        os.Exit(1)
    }
    defer res.Body.Close()

    body, _ := io.ReadAll(res.Body)
    fmt.Println("Status:", res.Status)
    fmt.Println("Resposta:", string(body))
}`;

      case 'java':
        return `// Java 11+ HttpClient nativo (sem dependências externas)
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class ApiConsumer {
    public static void main(String[] args) throws Exception {
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create("${fullUrl}"))
            .header("X-API-Key", "${apiKey}")
            .header("Accept", "application/json")${applicationId ? `\n            .header("X-Application-ID", "${applicationId}")` : ''}
            .GET()
            .build();

        HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());
        System.out.println("Status: " + response.statusCode());
        System.out.println("Payload: " + response.body());
    }
}`;

      case 'csharp':
        return `// C# / .NET 6+ com HttpClient
using System;
using System.Net.Http;
using System.Threading.Tasks;

class Program {
    static async Task Main() {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("X-API-Key", "${apiKey}");
        client.DefaultRequestHeaders.Add("Accept", "application/json");${applicationId ? `\n        client.DefaultRequestHeaders.Add("X-Application-ID", "${applicationId}");` : ''}

        var response = await client.GetAsync("${fullUrl}");
        var content = await response.Content.ReadAsStringAsync();

        Console.WriteLine($"Status: {response.StatusCode}");
        Console.WriteLine($"Resposta: {content}");
    }
}`;

      case 'curl':
      default:
        return `curl -X GET "${fullUrl}" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Accept: application/json"${applicationId ? ` \\\n  -H "X-Application-ID: ${applicationId}"` : ''}`;
    }
  };

  const code = getSnippet();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="bg-slate-900 dark:bg-slate-950 rounded-2xl border border-slate-800 shadow-xl overflow-hidden text-slate-200">
      {/* Header bar */}
      <div className="px-5 py-3.5 bg-slate-950/80 border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-amber-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
          </div>
          <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 ml-2">
            <Terminal className="w-3.5 h-3.5 text-primary-400" />
            Quick Start & Gerador de SDK
          </span>
        </div>

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-medium border border-slate-700 transition-all active:scale-95"
          title="Copiar código"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-400 font-semibold">Copiado!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copiar Snippet</span>
            </>
          )}
        </button>
      </div>

      {/* Language Selector Tabs */}
      <div className="px-4 py-2.5 bg-slate-950/40 border-b border-slate-800/60 overflow-x-auto custom-scrollbar flex items-center gap-1.5">
        {languages.map((lang) => (
          <button
            key={lang.id}
            onClick={() => setSelectedLanguage(lang.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
              selectedLanguage === lang.id
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* Code Display */}
      <div className="p-5 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto selection:bg-primary-500/30 selection:text-white max-h-96">
        <pre className="text-emerald-400 whitespace-pre">{code}</pre>
      </div>

      {/* Footer hint */}
      <div className="px-5 py-2.5 bg-slate-950/60 border-t border-slate-800/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-slate-400">
        <span className="flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          Credenciais e endpoints pré-preenchidos para integração instantânea.
        </span>
        <span className="text-slate-500 font-mono text-[10px]">Gateway: {resolvedGatewayUrl}</span>
      </div>
    </div>
  );
};
